import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { toast } from "react-toastify";
import { useThemePalette } from "common/hooks/theme_palette";
import {
  addPackageItemRequest,
  getAvailablePackageServicesRequest,
  getPackageDetailsRequest,
  getPackageItemsRequest,
  removePackageItemRequest,
  updatePackageItemRequest,
} from "services/modules/events/packages";

const KIND_OPTIONS = ["mandatory", "included", "additional"];

const kindChipSx = {
  mandatory: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    color: "#C62828",
  },
  included: {
    backgroundColor: "rgba(33, 150, 243, 0.1)",
    color: "#1565C0",
  },
  additional: {
    backgroundColor: "rgba(255, 152, 0, 0.12)",
    color: "#EF6C00",
  },
};

const PackageItemsPanel = ({
  packageId,
  eventTypeId,
  canUpdate,
  items: itemsProp,
  itemsCount,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { oliveGreen } = useThemePalette();
  const itemsFromStore = useSelector((state) => state.packages.items);
  const availableServices = useSelector(
    (state) => state.packages.availableServices,
  );
  const items = itemsProp?.length ? itemsProp : itemsFromStore || [];

  const [variantId, setVariantId] = useState("");
  const [kind, setKind] = useState("included");
  const [quantity, setQuantity] = useState(1);
  const [displayPriority, setDisplayPriority] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editKind, setEditKind] = useState("included");
  const [editQty, setEditQty] = useState(1);
  const [editPriority, setEditPriority] = useState(0);

  const refresh = () => {
    if (!packageId) return;
    dispatch(getPackageItemsRequest({ id: packageId }));
    dispatch(getPackageDetailsRequest({ id: packageId }));
    if (eventTypeId) {
      dispatch(
        getAvailablePackageServicesRequest({
          params: {
            package_id: packageId,
            event_type_id: eventTypeId,
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (!itemsProp?.length) {
      refresh();
    } else if (packageId && eventTypeId) {
      dispatch(
        getAvailablePackageServicesRequest({
          params: {
            package_id: packageId,
            event_type_id: eventTypeId,
          },
        }),
      );
    }
  }, [packageId, eventTypeId]);

  const handleAdd = () => {
    if (!variantId) return;
    dispatch(
      addPackageItemRequest({
        id: packageId,
        body: {
          item: {
            variant_id: Number(variantId),
            kind,
            quantity: Number(quantity) || 1,
            display_priority: Number(displayPriority) || 0,
          },
        },
        action: () => {
          toast.success(t("events_packages.item_added"));
          setVariantId("");
          setKind("included");
          setQuantity(1);
          setDisplayPriority(0);
          refresh();
        },
      }),
    );
  };

  const handleUpdate = (itemId) => {
    dispatch(
      updatePackageItemRequest({
        id: packageId,
        item_id: itemId,
        body: {
          item: {
            kind: editKind,
            quantity: Number(editQty) || 1,
            display_priority: Number(editPriority) || 0,
          },
        },
        action: () => {
          toast.success(t("events_packages.item_updated"));
          setEditingId(null);
          refresh();
        },
      }),
    );
  };

  const handleRemove = (itemId) => {
    dispatch(
      removePackageItemRequest({
        id: packageId,
        item_id: itemId,
        action: () => {
          toast.success(t("events_packages.item_removed"));
          refresh();
        },
      }),
    );
  };

  const startEdit = (item) => {
    const itemId = item?.item_id || item?.id;
    setEditingId(itemId);
    setEditKind(item?.kind || "included");
    setEditQty(item?.quantity ?? 1);
    setEditPriority(item?.display_priority ?? 0);
  };

  const linkedVariantIds = useMemo(
    () => new Set(items.map((i) => i?.variant_id)),
    [items],
  );

  const variantOptions = useMemo(() => {
    const options = [];
    (availableServices || []).forEach((service) => {
      (service?.options || []).forEach((opt) => {
        if (!opt?.variant_id || linkedVariantIds.has(opt.variant_id)) return;
        options.push({
          id: opt.variant_id,
          name: `${service?.name || ""} — ${
            opt?.option || t("events_packages.no_option")
          }`,
          display_priority: service?.display_priority ?? 0,
        });
      });
    });
    return options;
  }, [availableServices, linkedVariantIds, t]);

  const grouped = useMemo(() => {
    const groups = { mandatory: [], included: [], additional: [] };
    items.forEach((item) => {
      const key = KIND_OPTIONS.includes(item?.kind) ? item.kind : "included";
      groups[key].push(item);
    });
    return groups;
  }, [items]);

  const counts = {
    mandatory: itemsCount?.mandatory ?? grouped.mandatory.length,
    included: itemsCount?.included ?? grouped.included.length,
    additional: itemsCount?.additional ?? grouped.additional.length,
  };

  const renderItemCard = (item) => {
    const itemId = item?.item_id || item?.id;
    const isEditing = editingId === itemId;

    return (
      <Paper
        key={itemId}
        elevation={0}
        sx={{
          p: 2,
          border: "1px solid #E8E8E8",
          borderRadius: "10px",
          opacity: item?.available === false ? 0.85 : 1,
          transition: "border-color 0.15s ease",
          "&:hover": { borderColor: "#C8C8C8" },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          gap={2}
          alignItems={{ xs: "flex-start", md: "flex-start" }}
        >
          <Box
            sx={{
              width: 88,
              height: 72,
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "#F5F5F5",
              border: "1px solid #EEEEEE",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {item?.image ? (
              <img
                src={item.image}
                alt={item?.name || ""}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Typography fontSize="11px" color="#A0A0A0">
                {t("chat.no_image")}
              </Typography>
            )}
          </Box>

          <Box flex={1} width="100%">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
              mb={1}
            >
              <Box>
                <Typography fontSize="16px" fontWeight="600">
                  {item?.name || "-"}
                </Typography>
                <Typography fontSize="13px" color="#6A6A6A" mt={0.25}>
                  {item?.option || t("events_packages.no_option")}
                </Typography>
                <Stack
                  direction="row"
                  gap={0.75}
                  flexWrap="wrap"
                  mt={1}
                  alignItems="center"
                >
                  <Chip
                    size="small"
                    label={t(`events_packages.kinds.${item?.kind}`, {
                      defaultValue: item?.kind,
                    })}
                    sx={{
                      height: 22,
                      fontSize: "12px",
                      fontWeight: 600,
                      ...(kindChipSx[item?.kind] || {}),
                    }}
                  />
                  {item?.category?.name && (
                    <Chip
                      size="small"
                      label={item.category.name}
                      sx={{
                        height: 22,
                        fontSize: "12px",
                        backgroundColor: "rgba(33, 150, 243, 0.08)",
                        color: "#1565C0",
                      }}
                    />
                  )}
                  {item?.locked && (
                    <Chip
                      size="small"
                      label={t("events_packages.locked")}
                      sx={{ height: 22, fontSize: "12px" }}
                    />
                  )}
                  {item?.removable && (
                    <Chip
                      size="small"
                      color="info"
                      label={t("events_packages.removable")}
                      sx={{ height: 22, fontSize: "12px" }}
                    />
                  )}
                  <Chip
                    size="small"
                    label={
                      item?.available
                        ? t("common.available")
                        : t("common.not_available")
                    }
                    sx={{
                      height: 22,
                      fontSize: "12px",
                      backgroundColor: item?.available
                        ? "rgba(76, 175, 80, 0.12)"
                        : "rgba(244, 67, 54, 0.1)",
                      color: item?.available ? "#2E7D32" : "#C62828",
                    }}
                  />
                </Stack>
              </Box>

              {canUpdate && !isEditing && (
                <Stack direction="row" gap={0.5} flexShrink={0}>
                  <Button
                    size="small"
                    startIcon={<EditOutlinedIcon />}
                    onClick={() => startEdit(item)}
                    sx={{ textTransform: "none" }}
                  >
                    {t("common.edit")}
                  </Button>
                  <Button
                    color="error"
                    size="small"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => handleRemove(itemId)}
                    sx={{ textTransform: "none" }}
                  >
                    {t("common.delete")}
                  </Button>
                </Stack>
              )}
            </Stack>

            {isEditing ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                gap={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                mt={1}
              >
                <TextField
                  select
                  size="small"
                  label={t("events_packages.kind")}
                  value={editKind}
                  onChange={(e) => setEditKind(e.target.value)}
                  sx={{ minWidth: 150 }}
                >
                  {KIND_OPTIONS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {t(`events_packages.kinds.${k}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label={t("events_packages.quantity")}
                  value={editQty}
                  onChange={(e) => setEditQty(e.target.value)}
                  sx={{ width: 110 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t("events_packages.display_priority")}
                  value={editPriority}
                  onChange={(e) => setEditPriority(e.target.value)}
                  sx={{ width: 130 }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleUpdate(itemId)}
                  sx={{
                    backgroundColor: oliveGreen,
                    textTransform: "none",
                    "&:hover": { backgroundColor: oliveGreen, opacity: 0.9 },
                  }}
                >
                  {t("common.save")}
                </Button>
                <Button
                  size="small"
                  onClick={() => setEditingId(null)}
                  sx={{ textTransform: "none" }}
                >
                  {t("common.clear")}
                </Button>
              </Stack>
            ) : (
              <Stack
                direction="row"
                gap={3}
                flexWrap="wrap"
                mt={1.5}
                sx={{
                  pt: 1.25,
                  borderTop: "1px solid #F0F0F0",
                }}
              >
                <Box>
                  <Typography fontSize="11px" color="#8A8A8A">
                    {t("events_packages.quantity")}
                  </Typography>
                  <Typography fontSize="14px" fontWeight={600}>
                    {item?.quantity}
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize="11px" color="#8A8A8A">
                    {t("events_packages.unit_price")}
                  </Typography>
                  <Typography fontSize="14px" fontWeight={600}>
                    {item?.unit_price} {t("products.curr_sar")}
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize="11px" color="#8A8A8A">
                    {t("events_packages.amount")}
                  </Typography>
                  <Typography fontSize="14px" fontWeight={600}>
                    {item?.amount} {t("products.curr_sar")}
                  </Typography>
                </Box>
                <Box>
                  <Typography fontSize="11px" color="#8A8A8A">
                    {t("events_packages.display_priority")}
                  </Typography>
                  <Typography fontSize="14px" fontWeight={600}>
                    {item?.display_priority ?? 0}
                  </Typography>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{ p: 2.5, border: "1px solid #E8E8E8", borderRadius: "10px" }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
        mb={2.5}
      >
        <Box>
          <Typography fontSize="18px" fontWeight="600">
            {t("events_packages.items_title")}
          </Typography>
          <Typography fontSize="13px" color="#797979">
            {items.length} {t("events_packages.items_count_label")}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {KIND_OPTIONS.map((k) => (
            <Chip
              key={k}
              size="small"
              label={`${t(`events_packages.kinds.${k}`)}: ${counts[k]}`}
              sx={{ fontWeight: 600, ...(kindChipSx[k] || {}) }}
            />
          ))}
        </Stack>
      </Stack>

      {canUpdate && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: "8px",
            backgroundColor: "#FAFAFA",
            border: "1px dashed #DCDCDC",
          }}
        >
          {!eventTypeId ? (
            <Typography fontSize="14px" color="#8A8A8A">
              {t("events_packages.select_event_type_first")}
            </Typography>
          ) : (
            <Stack
              direction={{ xs: "column", md: "row" }}
              gap={1.5}
              alignItems={{ xs: "stretch", md: "center" }}
              flexWrap="wrap"
            >
              <TextField
                select
                size="small"
                label={t("events_packages.select_variant")}
                value={variantId}
                onChange={(e) => setVariantId(e.target.value)}
                sx={{ minWidth: 240, flex: 1, backgroundColor: "#fff" }}
              >
                {variantOptions.map((v) => (
                  <MenuItem key={v.id} value={v.id}>
                    {v.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label={t("events_packages.kind")}
                value={kind}
                onChange={(e) => setKind(e.target.value)}
                sx={{ minWidth: 150, backgroundColor: "#fff" }}
              >
                {KIND_OPTIONS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {t(`events_packages.kinds.${k}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="number"
                label={t("events_packages.quantity")}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                sx={{ width: { xs: "100%", md: 110 }, backgroundColor: "#fff" }}
              />
              <TextField
                size="small"
                type="number"
                label={t("events_packages.display_priority")}
                value={displayPriority}
                onChange={(e) => setDisplayPriority(e.target.value)}
                sx={{ width: { xs: "100%", md: 130 }, backgroundColor: "#fff" }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                disabled={!variantId}
                sx={{
                  backgroundColor: oliveGreen,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: oliveGreen, opacity: 0.9 },
                }}
              >
                {t("events_packages.add_item")}
              </Button>
            </Stack>
          )}
        </Paper>
      )}

      {items.length === 0 ? (
        <Box
          sx={{
            py: 5,
            textAlign: "center",
            borderRadius: "8px",
            backgroundColor: "#FAFAFA",
            border: "1px solid #F0F0F0",
          }}
        >
          <Typography color="#8A8A8A">
            {t("events_packages.items_empty")}
          </Typography>
        </Box>
      ) : (
        <Stack gap={2.5}>
          {KIND_OPTIONS.map((sectionKind) => {
            const sectionItems = grouped[sectionKind];
            if (!sectionItems.length) return null;
            return (
              <Box key={sectionKind}>
                <Typography
                  fontSize="14px"
                  fontWeight="600"
                  mb={1.25}
                  color="#4A4A4A"
                >
                  {t(`events_packages.kinds.${sectionKind}`)} (
                  {sectionItems.length})
                </Typography>
                <Stack gap={1.5}>{sectionItems.map(renderItemCard)}</Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

export default PackageItemsPanel;
